import * as React from "react";
import {
    DataGridPremium,
    getGridStringOperators,
    GRID_CHECKBOX_SELECTION_COL_DEF,
    GridColDef,
    GridColumnGroup,
    GridColumnGroupingModel,
    GridColumnVisibilityModel,
    GridFilterInputValueProps,
    GridFilterOperator,
    GridRenderCellParams,
    GridRowModel,
    GridSelectionModel,
    useGridApiRef,
    GridExcelExportOptions,
} from "@mui/x-data-grid-premium";
import {
    Alert,
    Checkbox,
    Chip,
    FormControlLabel,
    FormGroup,
    Link,
    Tooltip,
    Typography,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import AssessmentStatusGroupHeader from "./assessmentStatusGroupHeader";
import ScoringStatusCell from "./scoreStatusCell";
import AssessmentStatusDataGridToolbar from "./assessmentStatusDataGridToolbar";
import { useGetAppUrlsQuery } from "../../../../../../services/cclTokenedSettingsApi";
import {
    AccessEventRegistrationDetails,
    AccessEventSessionDetails,
} from "../../../../../../services/types/accessEventTypes";
import useLogAccessEvent from "../../../../../../hooks/useLogAccessEvent";
import SelfStatusCell, { SelfStatusCellProps } from "./selfStatusCell";
import CclDataGridActionMenu from "../../../../../../components/common/cclDataGridActionMenu/cclDataGridActionMenu";
import AssessmentStatusLegendDrawer from "./assessmentStatusLegendDrawer/assessmentStatusLegendDrawer";
import {
    ParticipantAssessmentStatus,
    ParticipantInstrumentStatus,
    ParticipantRaterInfo,
    SessionInstrument,
    SessionInstrumentRaterInfo,
} from "../../../../../../services/types/enterpriseParticipantApiTypes";
import RaterStatusCell from "./raterStatusCell";

export interface InstrumentVisibility {
    visible: boolean;
    id: number;
    name: string;
}

export interface AssessmentStatusDataGridProps {
    scoringMode: "none" | "request" | "score";
    isAdmin: boolean;
    instruments: SessionInstrument[];
    participants: ParticipantAssessmentStatus[];
    isLoading: boolean;
    sessionKey: number | undefined;
    sessionCode: string | undefined;
    isRegistration: boolean;
    onScorePressed: (selectedParticipantIds: number[]) => void;
    onRequestScorePressed: (selectedParticipantIds: number[]) => void;
    handleEvent: (event: string, participantIds: number[]) => Promise<void>;
    handleAdditionalClick: (esiKey: number) => void;
    assessmentVisibilityChange: (visible: boolean, instrumentId: number) => void;
    readOnly?: boolean;
    hideRaterActions: boolean;
    hideParticipantEmailActions: boolean;
    showSendParticipantsEmail: boolean;
    allowExport: boolean;
    showSnackbar: (message: string, severity: "error" | "warning" | "info" | "success") => void;
    isProductSale?: boolean;
    hideAboutMe?: boolean | null;
}

const AssessmentStatusDataGrid: React.FC<AssessmentStatusDataGridProps> = (props) => {
    const [showLegend, setShowLegend] = React.useState(false);
    const [currentEvent, setCurrentEvent] = React.useState<string | null>(null);
    const { logEvent } = useLogAccessEvent();
    const singleParticipant = props.participants.length === 1;
    const { data: urls, isSuccess } = useGetAppUrlsQuery();
    const [selectedParticipantIds, setSelectedParticipantIds] = React.useState<number[]>(
        singleParticipant
            ? props.participants.map((p) => {
                  return p.esiKey;
              })
            : [],
    );
    const [columnViewStatus, setColumnViewStatus] = React.useState<InstrumentVisibility[]>([]);
    const [columnVisibilityModel, setColumnVisibilityModel] =
        React.useState<GridColumnVisibilityModel>({});
    const apiRef = useGridApiRef();

    const raterColWidthWide = 100;
    const raterColWidthNarrow = 100;
    const selfAndScoreColWidth = 114;

    const STATUS_OPTIONS = [
        { label: "Not Accessed", value: "notAccessed" },
        { label: "Insufficient Raters", value: "insufficientRaters" },
        { label: "Incomplete", value: "incomplete" },
        { label: "Complete", value: "complete" },
    ];

    const assessmentsStatusOperator: GridFilterOperator = {
        label: "by status",
        value: "assessmentsStatus",
        getApplyFilterFn: (filterItem) => {
            if (!filterItem.value || filterItem.value.length === 0) return null;
            const selections = filterItem.value as string[];
            const filterFn = (params: GridRowModel) => {
                // don't filter anything out if no values selected

                const paxInstruments = params.row.instruments.filter(
                    (i: ParticipantInstrumentStatus) => i.raters.length > 1,
                ) as ParticipantInstrumentStatus[];

                // not accessed
                if (selections.includes("notAccessed")) {
                    if (paxInstruments.some((i) => i.selfAssessmentStatusTypeId === 1)) {
                        return true;
                    }
                }

                // not enough raters assigned
                if (selections.includes("insufficientRaters")) {
                    if (
                        paxInstruments.some((i) =>
                            i.raters.some((r) => r.minimumRequired > r.surveysSent),
                        )
                    ) {
                        return true;
                    }
                }

                // self or raters haven't finished
                if (selections.includes("incomplete")) {
                    // return true any self assessments haven't been completed (not accessed or in progress)
                    if (
                        paxInstruments.some(
                            (i) =>
                                i.selfAssessmentStatusTypeId === 1 ||
                                i.selfAssessmentStatusTypeId === 2,
                        )
                    ) {
                        return true;
                    }
                    // return true if the min number of raters haven't submitted
                    if (
                        paxInstruments.some((i) =>
                            i.raters.some((r) => r.receivedCount < r.minimumRequired),
                        )
                    ) {
                        return true;
                    }
                }

                // complete
                if (selections.includes("complete")) {
                    if (paxInstruments.some((i) => i.scoringStatus === 2)) {
                        return true;
                    }
                }

                return false; // Example logic
            };
            return filterFn;
        },
        InputComponent: (props: GridFilterInputValueProps) => {
            const value: string[] = props.item.value || [];
            const handleChange = (optionValue: string) => {
                const newValue = value.includes(optionValue)
                    ? value.filter((v) => v !== optionValue)
                    : [...value, optionValue];
                props.applyValue({ ...props.item, value: newValue });
            };
            return (
                <Box sx={{ padding: 2 }}>
                    <FormGroup>
                        {STATUS_OPTIONS.map((option) => (
                            <FormControlLabel
                                key={option.value}
                                control={
                                    <Checkbox
                                        checked={value.includes(option.value)}
                                        onChange={() => handleChange(option.value)}
                                    />
                                }
                                label={option.label}
                            />
                        ))}
                    </FormGroup>
                </Box>
            );
        },
    };

    function getRaterColumnId(instrumentId: number, raterCol: SessionInstrumentRaterInfo): string {
        return `${instrumentId}.${raterCol.raterTypeName.replace(/\s+/g, "").toLowerCase()}`;
    }

    function getRaterCellText(participant: any, instrumentId: number, raterId: number): string {
        if (participant.instruments.length === 0) return "";
        const instrument = participant.instruments.find(
            (i: ParticipantInstrumentStatus) => i.instrumentId === instrumentId,
        );
        if (!instrument) return "";
        const raterInfos = instrument.raters;
        const raterInfo = raterInfos.find((r: ParticipantRaterInfo) => r.raterTypeId === raterId);
        let cellText = `${raterInfo?.receivedCount ?? 0} of ${raterInfo?.surveysSent ?? 0}`;
        return raterInfo?.receivedLateCount > 0
            ? `${cellText} (${raterInfo.receivedLateCount} late)`
            : cellText;
    }

    function getSelfCellText(scored: boolean, status: number): string {
        switch (status) {
            case 1:
                return "Not Accessed";
            case 2:
                return "In Progress";
            case 3:
                return "Scoring";
            case 4:
                return scored ? "Scored" : "Complete";
            case 5:
            default:
                return "Cancelled";
        }
    }

    const colDefs: GridColDef[] = React.useMemo(() => {
        let columns: GridColDef[] = [];
        const moreColumnWidth = props.readOnly ? 60 : 50;

        const needMoreColumn =
            props.participants?.some((p) =>
                p.additionalInstruments?.some((i) => i.selfAssessmentStatusTypeId !== 5),
            ) ?? false;

        if (
            props?.instruments != null &&
            props.instruments.length > 0 &&
            columnViewStatus.length > 0
        ) {
            if (!singleParticipant) {
                columns = columns.concat([
                    {
                        ...GRID_CHECKBOX_SELECTION_COL_DEF,
                    },
                ]);
            }

            columns = columns.concat([
                {
                    field: "email",
                    headerAlign: "left",
                    headerName: "Email",
                    type: "string",
                    valueGetter: (params) => params.row.email,
                    minWidth: 200,
                    flex: 1.0,
                    hide: true,
                },
                {
                    field: "participant",
                    headerAlign: "left",
                    headerName: "Participant",
                    type: "string",
                    groupingValueGetter: (params) => {
                        return `${params.row.firstName} ${params.row.lastName}`;
                    },
                    valueGetter: (params) => {
                        return `${params.row.firstName} ${params.row.lastName}`;
                    },
                    renderCell: (params: GridRenderCellParams) => {
                        if (!props.isAdmin || props.isRegistration) {
                            return params.value;
                        } else {
                            return (
                                <Link href={`/registration/${params.row.esiKey}`}>
                                    {params.value}
                                </Link>
                            );
                        }
                    },
                    maxWidth: 275,
                    minWidth: 150,
                    flex: 1.0,
                    filterOperators: [...getGridStringOperators(), assessmentsStatusOperator],
                },
            ]);

            if (needMoreColumn) {
                columns = columns.concat([
                    {
                        field: "more-buttons",
                        headerName: "",
                        disableColumnMenu: true,
                        sortable: false,
                        filterable: false,
                        align: props.readOnly ? "center" : "right",
                        cellClassName: "moreBtnCell",
                        renderCell: (params: GridRenderCellParams) => {
                            if (!props.isAdmin) {
                                return params.value;
                            } else {
                                return params.row.additionalInstruments.some(
                                    (i: ParticipantInstrumentStatus) =>
                                        i.selfAssessmentStatusTypeId !== 5,
                                ) ? (
                                    <Chip
                                        label="More"
                                        color="primary"
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            float: "right",
                                            borderRadius: "16px",
                                            padding: "0px",
                                            margin: "0px",
                                        }}
                                        onClick={() => {
                                            props.handleAdditionalClick(params.row.esiKey);
                                        }}
                                    />
                                ) : null;
                            }
                        },
                        maxWidth: moreColumnWidth,
                        minWidth: moreColumnWidth,
                        // flex: 1.0,
                    },
                ]);
            }

            columns = columns.concat([
                {
                    field: "actions",
                    headerName: "",
                    width: 40,
                    disableColumnMenu: true,
                    sortable: false,
                    filterable: false,
                    renderCell: (params: GridRenderCellParams) => {
                        if (params.row.instruments.length === 0) return null;
                        return (
                            <CclDataGridActionMenu
                                id={params.row.esiKey.toString()}
                                menuItems={[
                                    {
                                        label: "View Assessment List",
                                        id: "viewassessmentlist",
                                        hide: props.readOnly || props.isRegistration,
                                    },
                                    {
                                        label: "View Contact Details",
                                        id: "viewparticipantcontactinfo",
                                    },
                                    {
                                        label: props.readOnly
                                            ? "View Participant Dates"
                                            : "Edit Participant Dates",
                                        id: "editkeydates",
                                    },
                                    {
                                        label: "Create Journal Entry",
                                        id: "createjournalentry",
                                        hide: props.readOnly,
                                    },
                                    {
                                        label: "Resend Invitation",
                                        id: "resendinvitation",
                                        hide: props.hideParticipantEmailActions,
                                    },
                                    {
                                        label: "Resend Reminder",
                                        id: "sendreminder",
                                        hide: props.hideParticipantEmailActions,
                                    },
                                    {
                                        label: "Resend Rater Invitations",
                                        id: "resendraterinvitations",
                                        hide: props.hideRaterActions,
                                    },
                                    {
                                        label: "Resend Rater Reminders",
                                        id: "sendraterreminders",
                                        hide: props.hideRaterActions,
                                    },
                                ]}
                                onSelect={async (val: string) => {
                                    await props.handleEvent(val, [params.row.esiKey]);
                                }}
                            />
                        );
                    },
                },
            ]);

            [...props.instruments]
                .sort((a, b) => {
                    if (a.hasRaters && !b.hasRaters) {
                        return 1;
                    } else if (!a.hasRaters && b.hasRaters) {
                        return -1;
                    } else {
                        return a.name.localeCompare(b.name);
                    }
                })
                .forEach((inst, index) => {
                    if (inst.hasRaters) {
                        // add the columns we know we need
                        if (!props.isProductSale) {
                            columns = columns.concat([
                                {
                                    field: `${inst.id}.scorable`,
                                    headerAlign: "center",
                                    headerName: "Scorability",
                                    headerClassName: "superHeader",
                                    cellClassName: "whiteCell",
                                    type: "string",
                                    renderCell: (params: GridRenderCellParams) => {
                                        if (params.row.instruments.length > 0) {
                                            return (
                                                <ScoringStatusCell
                                                    status={
                                                        getScorability(params.row, inst.id) ?? 0
                                                    }
                                                />
                                            );
                                        }
                                        return null;
                                    },
                                    maxWidth: selfAndScoreColWidth,
                                    minWidth: selfAndScoreColWidth,
                                    disableColumnMenu: true,
                                    sortable: false,
                                    filterable: false,
                                },
                            ]);
                        }
                        columns = columns.concat([
                            {
                                field: `${inst.id}.self`,
                                headerAlign: "center",
                                headerName: "Self",
                                headerClassName: "superHeader",
                                cellClassName: "whiteCell",
                                type: "string",
                                valueGetter: (params) => {
                                    if (params.row.instruments.length === 0) return "";
                                    const opts = getSelfStatusParams(params.row, inst.id);
                                    return getSelfCellText(opts.scored, opts.status);
                                },
                                renderCell: (params: GridRenderCellParams) => {
                                    if (params.row.instruments.length > 0) {
                                        return (
                                            <SelfStatusCell
                                                {...getSelfStatusParams(params.row, inst.id)}
                                            />
                                        );
                                    }
                                    return null;
                                },
                                maxWidth: selfAndScoreColWidth,
                                minWidth: selfAndScoreColWidth,
                                disableColumnMenu: true,
                                sortable: false,
                                filterable: false,
                            },
                        ]);
                        const raters =
                            props.instruments.find((i) => i.id === +inst.id)?.raters ?? [];

                        [...raters]
                            .filter((r) => r != null)
                            .forEach((r, index) => {
                                if (r.raterTypeId !== 1) {
                                    columns.push({
                                        field: getRaterColumnId(inst.id, r),
                                        headerAlign: "center",
                                        headerName: r.raterTypeName,
                                        renderHeader: () => {
                                            return (
                                                <Tooltip
                                                    title={
                                                        <div>
                                                            <Typography sx={{ fontSize: "1.25em" }}>
                                                                Minimum: {r.minimumRequired}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: "1.25em" }}>
                                                                Recommended: {r.minimumRecommended}
                                                            </Typography>
                                                        </div>
                                                    }
                                                >
                                                    <div
                                                        style={{
                                                            lineHeight: "1.5em",
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                justifyContent: "center",
                                                                display: "flex",
                                                            }}
                                                        >
                                                            {r?.raterTypeName}
                                                        </span>
                                                        <span
                                                            style={{
                                                                justifyContent: "center",
                                                                display: "flex",
                                                            }}
                                                        >{`${r.minimumRequired}/${r.minimumRecommended}`}</span>
                                                    </div>
                                                </Tooltip>
                                            );
                                        },
                                        headerClassName: "greyCellHeader",
                                        align: "left",
                                        cellClassName: "greyCell",
                                        type: "string",
                                        valueGetter: (params) =>
                                            getRaterCellText(params.row, inst.id, r.raterTypeId), // Used for Excel export
                                        renderCell: (params: GridRenderCellParams) => {
                                            if (params.row.instruments.length > 0) {
                                                return (
                                                    <RaterStatusCell
                                                        participant={params.row}
                                                        instrumentId={inst.id}
                                                        raterId={r.raterTypeId}
                                                        isWide={r?.raterTypeName.length > 10}
                                                    />
                                                );
                                            }
                                            return null;
                                        },
                                        minWidth:
                                            r?.raterTypeName.length > 10
                                                ? raterColWidthWide
                                                : raterColWidthNarrow,
                                        maxWidth:
                                            r?.raterTypeName.length > 10
                                                ? raterColWidthWide
                                                : raterColWidthNarrow,
                                        disableColumnMenu: true,
                                        sortable: false,
                                        filterable: false,
                                    });
                                }
                            });
                    } else {
                        columns = columns.concat([
                            {
                                field: `${inst.id}.self`,
                                headerAlign: "center",
                                headerName: "Self",
                                headerClassName: "superHeader",
                                cellClassName: "whiteCell",
                                type: "string",
                                valueGetter: (params) => {
                                    if (params.row.instruments.length === 0) return "";
                                    const opts = getSelfStatusParams(params.row, inst.id);
                                    return getSelfCellText(opts.scored, opts.status);
                                },
                                renderCell: (params: GridRenderCellParams) => {
                                    if (params.row.instruments.length > 0) {
                                        return (
                                            <SelfStatusCell
                                                {...getSelfStatusParams(params.row, inst.id)}
                                            />
                                        );
                                    }
                                    return null;
                                },
                                maxWidth: selfAndScoreColWidth,
                                minWidth: selfAndScoreColWidth,
                                disableColumnMenu: true,
                                sortable: false,
                                filterable: false,
                            },
                        ]);
                    }
                });
        }
        return columns;
    }, [props, columnViewStatus.length, singleParticipant]);

    const generateVisibilityModel = React.useCallback(() => {
        let newVisibilityModel: GridColumnVisibilityModel = { email: false };
        props.instruments.forEach((inst) => {
            const viewStatus = columnViewStatus.find((i) => i.id === inst.id);

            Object.assign(newVisibilityModel, {
                [`${inst.id}.self`]: viewStatus != null ? viewStatus.visible : true,
            });
            if (inst.hasRaters) {
                const raters = inst.raters.filter((r) => r.raterTypeName.toLowerCase() !== "self");

                if (!props.isProductSale) {
                    Object.assign(newVisibilityModel, {
                        [`${inst.id}.scorable`]: viewStatus?.visible ?? true,
                    });
                }
                if (raters.length > 0) {
                    raters.forEach((raterCol) => {
                        const raterColId = getRaterColumnId(inst.id, raterCol);
                        Object.assign(newVisibilityModel, {
                            [raterColId]: viewStatus?.visible ?? true,
                        });
                    });
                }
            }
        });
        return newVisibilityModel;
    }, [props.instruments, columnViewStatus]);

    React.useEffect(() => {
        if (props.instruments == null || props.instruments.length <= 0) {
            setColumnViewStatus([]);
            setColumnVisibilityModel({});
        } else if (columnViewStatus.length === 0) {
            var visibilities: InstrumentVisibility[] = props.instruments.map((i) => {
                return {
                    visible:
                        i.id === 2946
                            ? props.hideAboutMe != null
                                ? !props.hideAboutMe
                                : !(props.isProductSale ?? false)
                            : true,
                    id: i.id,
                    name: i.name,
                };
            });
            setColumnViewStatus(visibilities);
        }
        setColumnVisibilityModel(generateVisibilityModel());
    }, [columnViewStatus, generateVisibilityModel, props.instruments]);

    const getScorability = (
        pax: ParticipantAssessmentStatus,
        instrumentId: number,
    ): number | undefined => {
        const paxInstrument = pax.instruments.find((i) => i.instrumentId === +instrumentId);
        if (paxInstrument == null) return 0;
        if (paxInstrument.selfAssessmentStatusTypeId === 5) return 5;
        if (paxInstrument.scoringStatus === 2) return 2;
        if (paxInstrument.reportQualityTypeId === 4) return 1;
        return 0;
    };

    const handleEvent = async (buttonName: string) => {
        setCurrentEvent(buttonName);
        switch (buttonName) {
            case "showlegend":
                setShowLegend(true);
                setCurrentEvent(null);
                break;

            case "viewscoring":
                if (isSuccess && props.sessionKey != null) {
                    const evtData: AccessEventSessionDetails | AccessEventRegistrationDetails =
                        props.isRegistration
                            ? {
                                  esiKey: props.participants[0]?.esiKey.toString() ?? "unknown",
                                  imKey: props.participants[0]?.imKey.toString() ?? "unknown",
                                  email: props.participants[0]?.email ?? "unknown",
                              }
                            : {
                                  sessionID: props.sessionKey?.toString() ?? "unknown",
                                  projectCode: props.sessionCode ?? "unknown",
                              };
                    logEvent("AssessmentsScored", evtData);
                    setCurrentEvent(null);
                    window.open(
                        `${urls["CCLScoring"]}/program-status/${props.sessionKey}`,
                        "_blank",
                    );
                }
                break;

            case "score":
                props.onScorePressed(selectedParticipantIds);
                setCurrentEvent(null);
                break;

            case "export":
                if (selectedParticipantIds.length === 0) {
                    const visibleRows = apiRef.current.getVisibleRowModels();
                    await props
                        .handleEvent(
                            buttonName,
                            Array.from(visibleRows.keys()).map((id) => Number(id)),
                        )
                        .finally(() => setCurrentEvent(null));
                } else {
                    await props
                        .handleEvent(buttonName, selectedParticipantIds)
                        .finally(() => setCurrentEvent(null));
                }
                break;

            case "excelExport":
                const exportFields = colDefs
                    .map((col) => col.field)
                    .filter(
                        (field) =>
                            field !== "more-buttons" &&
                            field !== "actions" &&
                            field !== "__check__" &&
                            !field.endsWith(".scorable"),
                    );

                const date = new Date();
                const pad = (n: number) => n.toString().padStart(2, "0");
                const MM = pad(date.getUTCMonth() + 1);
                const DD = pad(date.getUTCDate());
                const YYYY = date.getUTCFullYear();
                const HH = pad(date.getUTCHours());
                const mm = pad(date.getUTCMinutes());
                const SS = pad(date.getUTCSeconds());
                const timestamp = `${MM}${DD}${YYYY}-${HH}${mm}${SS}`;
                const filename = props.sessionCode
                    ? `${props.sessionCode}-Assessment-Status-${timestamp}`
                    : `${props.sessionKey}-Assessment-Status-${timestamp}`;

                const exportOptions: GridExcelExportOptions = {
                    fileName: filename,
                    fields: exportFields,
                };

                apiRef.current
                    .exportDataAsExcel(exportOptions)
                    .then(() => {
                        const eventData =
                            props.participants.length === 1
                                ? ({
                                      esiKey: props.participants[0].esiKey.toString(),
                                      imKey: props.participants[0].imKey.toString(),
                                      email: props.participants[0].email,
                                  } as AccessEventRegistrationDetails)
                                : ({
                                      sessionID: props.sessionKey?.toString() ?? "",
                                      projectCode: props.sessionCode ?? "",
                                  } as AccessEventSessionDetails);

                        logEvent("AssessmentStatusExported", eventData);
                        props.showSnackbar("Assessment Status Exported", "success");
                    })
                    .catch((error) => {
                        props.showSnackbar("Assessment Status Export Failed", "error");
                    })
                    .finally(() => setCurrentEvent(null));
                break;

            case "requestscoring":
                props.onRequestScorePressed(
                    selectedParticipantIds.length > 0
                        ? selectedParticipantIds
                        : props.participants.map((p) => p.esiKey),
                );
                setCurrentEvent(null);
                break;
            case "createjournalentry":
            case "resendinvitations":
            case "sendreminders":
            case "resendraterinvitations":
            case "sendraterreminders":
            default:
                await props
                    .handleEvent(buttonName, selectedParticipantIds)
                    .finally(() => setCurrentEvent(null));
                break;
        }
    };

    const getSelfStatusParams = (
        pax: ParticipantAssessmentStatus,
        instrumentId: number,
    ): SelfStatusCellProps => {
        const paxInstrument = pax.instruments.find((i) => i.instrumentId === +instrumentId);
        const st = paxInstrument?.selfAssessmentStatusTypeId ?? 0;
        const numRaters = paxInstrument?.supportedRaterTypeCount ?? 0;
        const scrd =
            paxInstrument?.scoringStatus == null ? false : paxInstrument.scoringStatus === 2;
        return { status: st, hasRaters: numRaters > 1, scored: scrd };
    };

    const assessmentVisibilityChange = (visible: boolean, instrumentId: number) => {
        const inst = props.instruments.find((i) => i.id === instrumentId);
        props.assessmentVisibilityChange(visible, instrumentId);
        if (inst == null) return;
        let newViewStatus = columnViewStatus.filter((vs) => vs.id !== instrumentId);
        let colViewStatus = columnViewStatus.find((vs) => vs.id === instrumentId);
        if (colViewStatus == null) {
            newViewStatus.push({
                id: inst.id,
                name: inst.name,
                visible: visible,
            });
        } else {
            newViewStatus.push({
                id: colViewStatus.id,
                name: colViewStatus.name,
                visible: visible,
            });
        }
        setColumnViewStatus(newViewStatus);
    };

    let colGroupingModel: GridColumnGroupingModel = [];

    if (props.instruments.length > 0) {
        let columnGroup: GridColumnGroup[] = props.instruments.map((inst, index) => {
            let children = [{ field: `${inst.id}.self` }];
            if (inst.hasRaters) {
                if (!props.isProductSale) children.push({ field: `${inst.id}.scorable` });
                const raters = props.instruments.find((i) => i.id === +inst.id)?.raters ?? [];

                raters.forEach((r) => {
                    children.push({
                        field: getRaterColumnId(inst.id, r),
                    });
                });
            }

            return {
                groupId: `${inst.shortName}-${inst.id}`,
                children: children,
                headerAlign: "center",
                headerClassName: "superHeader",
                headerName: inst.name,
                renderHeaderGroup: (params) => {
                    return (
                        <AssessmentStatusGroupHeader
                            {...params}
                            headerText={inst.hasRaters ? inst.name : inst.shortName}
                            hoverTitle={`${inst.name} (${inst.shortName})`}
                        />
                    );
                },
            };
        });
        colGroupingModel = columnGroup.sort((a: GridColumnGroup, b: GridColumnGroup) =>
            a.groupId.localeCompare(b.groupId),
        );
    }

    if (
        props.instruments == null ||
        props.instruments.length === 0 ||
        props.participants == null ||
        props.participants.length === 0
    ) {
        return (
            <Stack width={1} spacing={2}>
                <Alert severity="info">
                    No assessment status information found for this session.
                </Alert>
            </Stack>
        );
    }
    const sortedParticipants = [...props.participants].sort((a, b) =>
        a.firstName.localeCompare(b.firstName),
    );

    return (
        <Stack width={1} spacing={2}>
            {showLegend ? (
                <AssessmentStatusLegendDrawer
                    open={showLegend}
                    onClose={() => setShowLegend(false)}
                />
            ) : null}
            <DataGridPremium
                apiRef={apiRef}
                getRowId={(row) => row.esiKey}
                experimentalFeatures={{ columnGrouping: true }}
                loading={props.isLoading}
                components={{ Toolbar: AssessmentStatusDataGridToolbar }}
                componentsProps={{
                    toolbar: {
                        scoringMode: props.scoringMode,
                        isRegistration: props.isRegistration,
                        numSelected: selectedParticipantIds.length,
                        handleButtonClick: async (buttonName: string) => handleEvent(buttonName),
                        instrumentVisibility: columnViewStatus,
                        onAssessmentFilterChange: assessmentVisibilityChange,
                        readOnly: props.readOnly,
                        sessionCode: props.sessionCode,
                        sessionKey: props.sessionKey,
                        inProgressEvent: currentEvent,
                        hideRaterActions: props.hideRaterActions,
                        hideParticipantEmailActions: props.hideParticipantEmailActions,
                        showSendParticipantsEmail: props.showSendParticipantsEmail,
                        showExport: props.allowExport && !props.readOnly,
                    },
                }}
                onSelectionModelChange={(ids: GridSelectionModel) => {
                    setSelectedParticipantIds([...ids.map((i) => i as number)]);
                }}
                checkboxSelection={!singleParticipant}
                columnGroupingModel={colGroupingModel}
                columnVisibilityModel={columnVisibilityModel}
                onFilterModelChange={(newModel) => {
                    console.log("filter model changed", newModel);
                }}
                columns={colDefs}
                rows={sortedParticipants}
                disableSelectionOnClick={true}
                sx={{
                    "& .MuiDataGrid-columnSeparator": {
                        visibility: "hidden",
                    },
                    "& .greyCell": {
                        backgroundColor: "#F0F1F3",
                        paddingLeft: "2px",
                    },
                    "& .moreBtnCell": {
                        paddingLeft: "0px",
                        paddingRight: "0px",
                    },
                    "& .greyCellHeader": {
                        backgroundColor: "#F0F1F3",
                        borderLeft: "1px solid #E0E0E0",
                        paddingLeft: "2px",
                        paddingRight: "2px",
                        fontWeight: "bold",
                    },
                    "& .superHeader": {
                        borderLeft: "1px solid #E0E0E0",
                        fontWeight: "bold",
                    },
                    "& .whiteCell": {
                        paddingLeft: "4px",
                    },
                    "& .MuiDataGrid-pinnedColumns": {
                        backgroundColor: "#FFFFFF",
                    },
                    "& .MuiDataGrid-pinnedColumnHeaders": {
                        backgroundColor: "#FFFFFF",
                    },
                }}
                autoHeight
                initialState={{
                    sorting: { sortModel: [{ field: "name", sort: "asc" }] },
                    pinnedColumns: {
                        left: ["__check__", "participant", "more-buttons", "actions"],
                    },
                }}
                disableRowGrouping
                disableExtendRowFullWidth
            />
        </Stack>
    );
};

export default AssessmentStatusDataGrid;
